import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { InfoCard, FormDatePicker, DateRangeInput, InfoItem } from './CommonComponents';

/**
 * 개선된 수주품의서 기본정보 입력 폼 (V2)
 * 
 * 개선사항:
 * - 섹션별 카드 분리
 * - 날짜 범위 입력 (시작/종료 연동)
 * - 보증보험 비율 입력
 * - 반응형 그리드
 */
const OrderBookFormV2 = ({ formik, orderInfo, project }) => {
  // 프로젝트 타입이 신규(N)인지 확인
  const isNewProject = project?.prjTyp === 'N';

  return (
    <Box>
      {/* 프로젝트 기본 정보 (읽기 전용) */}
      <InfoCard title="프로젝트 정보" icon={<DescriptionIcon />}>
        <Grid container spacing={3}>
          <InfoItem 
            label="프로젝트 번호" 
            value={orderInfo?.prjNo} 
            xs={12} sm={6} md={3}
            highlight
          />
          <InfoItem 
            label="사업명" 
            value={orderInfo?.cstNm || orderInfo?.prjNm} 
            xs={12} sm={6} md={3}
          />
          <InfoItem 
            label="매출처" 
            value={orderInfo?.partnerNm} 
            xs={12} sm={6} md={3}
          />
          <InfoItem 
            label="매출처 담당자" 
            value={orderInfo?.employeeNm} 
            xs={12} sm={6} md={3}
          />
        </Grid>
      </InfoCard>

      {/* 계약 정보 */}
      <InfoCard title="계약 정보" icon={<CalendarIcon />}>
        <Grid container spacing={3}>
          {/* 계약일 / 계약완료일 */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              계약 기간
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormDatePicker
                name="expDay"
                label="계약일"
                formik={formik}
                required
              />
              <FormDatePicker
                name="siDt"
                label="계약완료일"
                formik={formik}
                required
              />
            </Stack>
          </Grid>

          {/* 납품예정일 */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              납품
            </Typography>
            <FormDatePicker
              name="dlvryDuedate"
              label="납품예정일"
              formik={formik}
            />
          </Grid>

          {/* 검수조건 */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              검수
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="inspectCond"
              name="inspectCond"
              label="검수조건"
              value={formik.values.inspectCond || ''}
              onChange={formik.handleChange}
              error={formik.touched.inspectCond && Boolean(formik.errors.inspectCond)}
              helperText={formik.touched.inspectCond && formik.errors.inspectCond}
            />
          </Grid>
        </Grid>

        {/* 무상유지보수 기간 (신규 프로젝트인 경우만) */}
        {isNewProject && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              무상 유지보수 기간
            </Typography>
            <DateRangeInput
              startName="mtnFreeStrtDt"
              endName="mtnFreeEndDt"
              startLabel="시작일"
              endLabel="종료일"
              formik={formik}
              required={isNewProject}
            />
          </>
        )}

        {/* 라이센스 기간 */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          라이센스 기간
        </Typography>
        <DateRangeInput
          startName="licStrtDt"
          endName="licEndDt"
          startLabel="시작일"
          endLabel="종료일"
          formik={formik}
        />
      </InfoCard>

      {/* 결제 조건 */}
      <InfoCard title="결제 조건" icon={<PaymentIcon />}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              id="collCondt"
              name="collCondt"
              label="수금조건"
              value={formik.values.collCondt || ''}
              onChange={formik.handleChange}
              error={formik.touched.collCondt && Boolean(formik.errors.collCondt)}
              helperText={formik.touched.collCondt && formik.errors.collCondt}
              placeholder="예: 세금계산서 발행 후 익월말"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              id="payCondt"
              name="payCondt"
              label="지급조건"
              value={formik.values.payCondt || ''}
              onChange={formik.handleChange}
              error={formik.touched.payCondt && Boolean(formik.errors.payCondt)}
              helperText={formik.touched.payCondt && formik.errors.payCondt}
              placeholder="예: 현금"
            />
          </Grid>
        </Grid>
      </InfoCard>

      {/* 보증보험 */}
      <InfoCard title="보증보험" icon={<SecurityIcon />}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              id="giPrePct"
              name="giPrePct"
              label="선급금 비율 (%)"
              value={formik.values.giPrePct || ''}
              onChange={formik.handleChange}
              error={formik.touched.giPrePct && Boolean(formik.errors.giPrePct)}
              helperText={formik.touched.giPrePct && formik.errors.giPrePct}
              InputProps={{
                inputProps: { min: 0, max: 100 }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              id="giCovPct"
              name="giCovPct"
              label="계약이행 비율 (%)"
              value={formik.values.giCovPct || ''}
              onChange={formik.handleChange}
              error={formik.touched.giCovPct && Boolean(formik.errors.giCovPct)}
              helperText={formik.touched.giCovPct && formik.errors.giCovPct}
              InputProps={{
                inputProps: { min: 0, max: 100 }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              id="giMtnPct"
              name="giMtnPct"
              label="하자유지보수 비율 (%)"
              value={formik.values.giMtnPct || ''}
              onChange={formik.handleChange}
              error={formik.touched.giMtnPct && Boolean(formik.errors.giMtnPct)}
              helperText={formik.touched.giMtnPct && formik.errors.giMtnPct}
              InputProps={{
                inputProps: { min: 0, max: 100 }
              }}
            />
          </Grid>
        </Grid>
      </InfoCard>

      {/* 참고사항 */}
      <InfoCard title="참고사항" icon={<CommentIcon />}>
        <TextField
          fullWidth
          multiline
          rows={4}
          id="comment"
          name="comment"
          label="참고사항"
          value={formik.values.comment || ''}
          onChange={formik.handleChange}
          error={formik.touched.comment && Boolean(formik.errors.comment)}
          helperText={formik.touched.comment && formik.errors.comment}
          placeholder="수주품의서에 대한 참고사항을 입력하세요..."
        />
      </InfoCard>
    </Box>
  );
};

export default OrderBookFormV2;
